// Voice-to-text hook using browser Web Speech API
// Supports Hindi (hi-IN) + English (en-IN) auto-detect via language toggle.
import { useEffect, useRef, useState, useCallback } from 'react';

export function useSpeechRecognition({ lang = 'hi-IN', onResult } = {}) {
  const [listening, setListening] = useState(false);
  const [supported, setSupported] = useState(false);
  const [error, setError] = useState(null);
  const recRef = useRef(null);

  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    setSupported(!!SR);
  }, []);

  const start = useCallback(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      setError('Speech recognition not supported in this browser. Use Chrome.');
      return;
    }
    try {
      const rec = new SR();
      rec.lang = lang;
      rec.continuous = false;
      rec.interimResults = true;
      let finalText = '';
      rec.onresult = (event) => {
        let interim = '';
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const r = event.results[i];
          if (r.isFinal) finalText += r[0].transcript;
          else interim += r[0].transcript;
        }
        onResult?.({ final: finalText, interim, combined: finalText + interim });
      };
      rec.onerror = (e) => {
        setError(e.error || 'speech_error');
        setListening(false);
      };
      rec.onend = () => {
        setListening(false);
      };
      rec.start();
      recRef.current = rec;
      setListening(true);
      setError(null);
    } catch (e) {
      setError(e.message);
      setListening(false);
    }
  }, [lang, onResult]);

  const stop = useCallback(() => {
    try { recRef.current?.stop(); } catch { /* noop */ }
    setListening(false);
  }, []);

  return { listening, supported, error, start, stop };
}
